import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="terms-of-service-page">
      <Link to="/">
        <Button variant="ghost" className="mb-6 gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Button>
      </Link>

      <div className="gradient-card rounded-2xl border border-white/20 dark:border-white/10 p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold gradient-text">Terms of Service</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Last updated: January 2025</p>
          </div>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-600 dark:text-slate-300">
          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing and using Habit2Goals ("the App"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the App. The App is owned and operated by Tiara Consultants - AI Product Dev Division.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">2. Description of Service</h2>
            <p>Habit2Goals is a habit tracking and personal productivity application that provides:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Daily habit tracking with customizable habits</li>
              <li>Streak tracking and completion statistics</li>
              <li>Calendar views for progress monitoring</li>
              <li>Analytics and performance charts</li>
              <li>Data export functionality (CSV)</li>
              <li>Social sharing features for accountability</li>
              <li>Cloud synchronization across devices (Premium)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">3. User Accounts</h2>
            <p>
              To use certain features of the App, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">4. Subscription and Payments</h2>
            <p>
              Habit2Goals offers both free and premium subscription tiers:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Free Trial:</strong> 7-day trial with access to premium features</li>
              <li><strong>Premium Subscription:</strong> US$2.99/month for unlimited access to all features</li>
              <li>Payments are processed securely through Stripe</li>
              <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
              <li>Refunds are handled according to our refund policy and applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">5. User Content and Data</h2>
            <p>
              You retain ownership of all habit data and content you create within the App. By using the App, you grant us 
              a limited license to store, process, and display your data solely for the purpose of providing the service.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>You can export your data at any time via CSV export</li>
              <li>You can delete your account and associated data upon request</li>
              <li>We do not sell your personal data to third parties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Use the App for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>Share your account credentials with others</li>
              <li>Use automated scripts or bots to access the App</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">7. Medical Disclaimer</h2>
            <p className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <strong>Important:</strong> Habit2Goals is designed for habit management and personal productivity purposes only. 
              It is NOT intended to provide medical advice, diagnosis, or treatment. The App should not be used as a substitute 
              for professional medical advice. Always consult with a qualified healthcare provider regarding any health-related habits or concerns.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Tiara Consultants shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages arising from your use of the App. Our total liability shall not 
              exceed the amount you paid for the service in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">9. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes via 
              email or in-app notification. Continued use of the App after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">10. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact:<br />
              <strong>Tiara Consultants - AI Product Dev Division</strong><br />
              Email/Suggestions: <a href="mailto:support@habit2goals.online" className="text-blue-600 dark:text-blue-400 hover:underline">support@habit2goals.online</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
